import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, BarChart3, FileText, Users, Globe, Target, Search, Activity, MessageSquare, Layers, Brain, CheckCircle2 } from 'lucide-react';

const FEATURES = [
  { icon: Activity, title: 'Projectstatus', desc: 'Live status van uw lopende opdrachten, milestones en deadlines.' },
  { icon: FileText, title: 'Onboarding', desc: 'Stap-voor-stap onboarding zodat wij u perfect kunnen ondersteunen.' },
  { icon: Layers, title: 'Gekoppelde accounts', desc: 'Inzicht in alle gekoppelde accounts (Google, Meta, TikTok, LinkedIn).' },
  { icon: Target, title: 'Google Ads statistieken', desc: 'Real-time kosten, conversies, ROI en optimalisatieadvies.' },
  { icon: Globe, title: 'Social Ads statistieken', desc: 'Facebook, Instagram, LinkedIn en TikTok performance op één plek.' },
  { icon: Search, title: 'SEO prestaties', desc: 'Posities, klikken, vertoningen en organisch verkeer per maand.' },
  { icon: BarChart3, title: 'Website conversies', desc: 'Inzicht in verkeer, gedrag en conversies — gekoppeld aan uw doelen.' },
  { icon: Users, title: 'Leads', desc: 'Alle leads die binnenkomen, automatisch verzameld en gerubriceerd.' },
  { icon: MessageSquare, title: 'Content planning', desc: 'Wat wanneer wordt gepubliceerd — inclusief preview en goedkeuring.' },
  { icon: Brain, title: 'AI inzichten', desc: 'Automatische analyses, kansen en aanbevelingen voor groei.' },
  { icon: FileText, title: 'Rapportages', desc: 'Maandelijkse PDF rapporten + live overzicht in het dashboard.' },
];

export default function KlantdashboardPage() {
  return (
    <>
      <SEOHead
        title="Klantdashboard — Volledig Inzicht in Uw Marketing"
        description="Het IMPERIA AI klantdashboard geeft u real-time inzicht in alle marketingresultaten: Google Ads, social ads, SEO, leads, content en AI inzichten."
        canonical="/klantdashboard"
        keywords="klantdashboard marketing, marketing dashboard, rapportage marketing, marketing inzicht"
      />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)', padding: '120px 0 80px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span style={{ color: '#D4A017', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Klantdashboard</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6" style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.1 }}>
                Volledig inzicht in <span style={{ color: '#D4A017' }}>uw marketing</span> — altijd
              </h1>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
                Wij verbergen niets. In uw eigen klantdashboard ziet u real-time wat we doen, hoe het presteert en wat de volgende stap is. Van Google Ads tot SEO tot leads — alles op één plek.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register" data-testid="dashboard-cta-offerte" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-3.5 rounded-xl">
                  Start gratis scan <ArrowRight size={16} />
                </Link>
                <Link to="/werkwijze" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-7 py-3.5 rounded-xl">
                  Bekijk werkwijze
                </Link>
              </div>
            </div>
            {/* Dashboard mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-4 border-white/10 overflow-hidden">
                <div className="bg-[#0f172a] px-4 py-2.5 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 bg-red-400 rounded-full" />
                    <span className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />
                    <span className="w-2.5 h-2.5 bg-green-400 rounded-full" />
                  </div>
                  <p data-testid="dashboard-preview-brand" className="text-white/60 text-xs ml-2">IMPERIA AI Dashboard</p>
                </div>
                <div className="p-5 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { l: 'Leads', v: '42', c: '#16a34a' },
                      { l: 'Conversie', v: '5.8%', c: '#D4A017' },
                      { l: 'ROAS', v: '4.2x', c: '#7c3aed' },
                    ].map(k => (
                      <div key={k.l} className="bg-slate-50 rounded-lg p-2.5">
                        <p className="text-[9px] text-slate-500 uppercase font-semibold">{k.l}</p>
                        <p className="text-base font-extrabold" style={{ color: k.c }}>{k.v}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1.5">Google Ads — laatste 30 dagen</p>
                    <div className="flex items-end gap-1 h-12">
                      {[40, 55, 48, 65, 72, 60, 78, 88, 70, 92, 85, 100].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t bg-[#D4A017]" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[9px] text-slate-500 uppercase font-semibold">SEO posities</p>
                      <p className="text-sm font-bold text-[#0f172a]">↑ 14 ↑ in top-10</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[9px] text-slate-500 uppercase font-semibold">Social bereik</p>
                      <p className="text-sm font-bold text-[#0f172a]">28.4k impressies</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#D4A017] text-[#0f172a] font-bold px-3 py-1.5 rounded-lg shadow-lg text-xs">
                LIVE DATA
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="bg-white py-20">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#D4A017] text-xs font-bold uppercase tracking-widest">Wat ziet u in uw dashboard?</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mt-3 mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>Alle marketing op één plek</h2>
            <p className="text-[#64748b] text-base max-w-2xl mx-auto">Geen losse rapporten meer, geen toegangen kwijtraken, geen onduidelijkheid. Eén centrale plek voor uw hele marketing.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`dashboard-feature-${i}`} className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-[#D4A017]/30 transition-all">
                  <div className="w-11 h-11 rounded-xl bg-[#D4A017]/10 flex items-center justify-center mb-3">
                    <Icon size={20} className="text-[#D4A017]" />
                  </div>
                  <h3 className="font-bold text-[#0f172a] text-base mb-1.5">{f.title}</h3>
                  <p className="text-sm text-[#64748b] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-[#fff8e1] py-16">
        <div className="container-custom max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] text-center mb-8" style={{ fontFamily: "'Sora', sans-serif" }}>
            Volledig transparant — en u behoudt alle controle
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Uw advertentieaccounts en data blijven uw eigendom',
              'Geen verborgen processen — u ziet wat wij doen',
              'Wekelijkse en maandelijkse rapportage',
              'Direct contact met uw projectmanager via chat',
              'Toegang voor uw team en partners',
              'Export van rapporten naar PDF en Excel',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-[#D4A017]/20 rounded-xl p-5">
                <CheckCircle2 size={20} className="text-[#D4A017] shrink-0 mt-0.5" />
                <span className="text-[#0f172a] font-medium text-sm md:text-base">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f172a] py-20">
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Klaar voor volledig inzicht?
          </h2>
          <p className="text-white/70 mb-8 text-base md:text-lg">
            Vraag een gratis scan aan en ontdek hoeveel meer u uit uw marketing kunt halen.
          </p>
          <Link to="/register" data-testid="dashboard-bottom-cta" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-8 py-4 rounded-xl text-lg">
            Start gratis scan <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
